import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Skeleton, Select, DatePicker, Progress, Empty, Tooltip, Tabs, Button, message } from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  ShoppingOutlined,
  LikeOutlined,
  DislikeOutlined,
  PieChartOutlined,
  LineChartOutlined,
  VideoCameraOutlined,
  UserOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  EyeOutlined,
  MessageOutlined,
  HeartOutlined,
  DownloadOutlined,
  FilterOutlined,
  CalendarOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { WaitingAnalyticsService, WaitingAnalytics, WAITING_TYPES } from '../services/waitingAnalyticsService';
import { 
  feedbackAnalyticsService, 
  FeedbackAnalytics, 
  DISLIKE_REASON_LABELS 
} from '../services/feedbackAnalyticsService';
import { getActiveBrandId } from '../config/api';
import type { Dayjs } from 'dayjs';
import './UnifiedAnalytics.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AnimatedNumber: React.FC<{ value: number; duration?: number; suffix?: string; decimals?: number }> = ({ 
  value, 
  duration = 1000, 
  suffix = '',
  decimals = 0 
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setDisplayValue(progress * value);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  const formatted = decimals > 0 
    ? displayValue.toFixed(decimals)
    : Math.floor(displayValue).toLocaleString();

  return <span>{formatted}{suffix}</span>;
};

const formatLargeNumber = (num: number): string => {
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toLocaleString();
};

const UnifiedAnalyticsPage: React.FC = () => {
  const { user } = useAuth();
  const [waitingAnalytics, setWaitingAnalytics] = useState<WaitingAnalytics | null>(null);
  const [feedbackAnalytics, setFeedbackAnalytics] = useState<FeedbackAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [productType, setProductType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const activeBrandId = getActiveBrandId(user);

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandId, productType, dateRange]);

  const loadAnalytics = async () => {
    if (!activeBrandId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
      const endDate = dateRange?.[1]?.format('YYYY-MM-DD');
      
      const [waitingData, feedbackData] = await Promise.all([
        WaitingAnalyticsService.getAnalytics(activeBrandId, productType, startDate, endDate),
        feedbackAnalyticsService.getAnalytics(activeBrandId, undefined, startDate, endDate, productType)
      ]);

      setWaitingAnalytics(waitingData);
      setFeedbackAnalytics(feedbackData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadWeeklyReport = async () => {
    if (!activeBrandId) return;
    
    try {
      setDownloadingReport(true);
      await WaitingAnalyticsService.downloadWeeklyReport(activeBrandId, productType);
      message.success('Haftalık rapor başarıyla indirildi');
    } catch (error) {
      console.error('Failed to download report:', error);
      message.error('Rapor indirilemedi. Lütfen tekrar deneyin.');
    } finally {
      setDownloadingReport(false);
    }
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 70) return '#10B981';
    if (percentage >= 40) return '#F59E0B';
    return '#EC4899';
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `0:${Math.round(seconds).toString().padStart(2, '0')}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}s`;
  };

  const getEntityTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <VideoCameraOutlined />;
      case 'avatar': return <UserOutlined />;
      case 'combination': return <AppstoreOutlined />;
      default: return <PieChartOutlined />;
    }
  };

  const getReasonLabel = (reason: string): string => {
    return DISLIKE_REASON_LABELS[reason] || reason;
  };

  if (loading) {
    return (
      <div className="unified-analytics">
        <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 24 }} />
        <Row gutter={[16, 16]}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Col xs={24} sm={12} lg={4} xl={4} key={i}>
              <Card style={{ borderRadius: 20 }}><Skeleton active /></Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  const hasWaitingData = waitingAnalytics && waitingAnalytics.sessions.totalSessions > 0;
  const hasFeedbackData = feedbackAnalytics && feedbackAnalytics.totalFeedbacks > 0;

  return (
    <div className="unified-analytics">
      {/* Header */}
      <div className="unified-analytics__header">
        <div>
          <Title level={2} className="unified-analytics__header-title">
            <BarChartOutlined style={{ color: '#6366F1' }} />
            Analitik Merkezi
          </Title>
          <Text className="unified-analytics__header-subtitle">Tüm metrikleriniz tek bir sayfada</Text>
        </div>
        
        <div className="unified-analytics__filters">
          <Select
            value={productType}
            onChange={setProductType}
            style={{ width: 160 }}
            options={WAITING_TYPES}
            suffixIcon={<CalendarOutlined />}
            placeholder="Son 30 Gün"
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD.MM.YYYY"
            placeholder={['Başlangıç', 'Bitiş']}
          />
          <Button icon={<FilterOutlined />}>Filtrele</Button>
        </div>
      </div>

      {/* Summary Cards - 5 columns */}
      <Row gutter={[16, 16]} className="unified-analytics__summary">
        <Col xs={24} sm={12} lg={4} xl={4}>
          <Card className="summary-card summary-card--sessions">
            <div className="summary-card__content">
              <div className="summary-card__info">
                <Text className="summary-card__label">Toplam Oturum</Text>
                <Title level={3} className="summary-card__value">
                  {formatLargeNumber(waitingAnalytics?.sessions.totalSessions || 0)}
                </Title>
              </div>
              <div className="summary-card__icon">
                <RiseOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5} xl={5}>
          <Card className="summary-card summary-card--completion">
            <div className="summary-card__content">
              <div className="summary-card__info">
                <Text className="summary-card__label">Tamamlanma</Text>
                <Title level={3} className="summary-card__value">
                  <AnimatedNumber value={waitingAnalytics?.sessions.completionRate || 0} suffix="%" decimals={1} />
                </Title>
              </div>
              <div className="summary-card__icon">
                <CheckCircleOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5} xl={5}>
          <Card className="summary-card summary-card--feedback">
            <div className="summary-card__content">
              <div className="summary-card__info">
                <Text className="summary-card__label">Geri Bildirim</Text>
                <Title level={3} className="summary-card__value">
                  {formatLargeNumber(feedbackAnalytics?.totalFeedbacks || 0)}
                </Title>
              </div>
              <div className="summary-card__icon">
                <MessageOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5} xl={5}>
          <Card className="summary-card summary-card--likes">
            <div className="summary-card__content">
              <div className="summary-card__info">
                <Text className="summary-card__label">Beğeni Oranı</Text>
                <Title level={3} className="summary-card__value">
                  <AnimatedNumber value={feedbackAnalytics?.likeRate || 0} suffix="%" decimals={1} />
                </Title>
              </div>
              <div className="summary-card__icon">
                <HeartOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5} xl={5}>
          <Card className="summary-card summary-card--time">
            <div className="summary-card__content">
              <div className="summary-card__info">
                <Text className="summary-card__label">Ort. Bekleme</Text>
                <Title level={3} className="summary-card__value">
                  {formatDuration(waitingAnalytics?.sessions.avgWaitingTime || 0)}
                </Title>
              </div>
              <div className="summary-card__icon">
                <ClockCircleOutlined />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: (
              <span>
                <ClockCircleOutlined />
                Oturum Metrikleri
              </span>
            ),
            children: (
              <>
                {!hasWaitingData ? (
                  <Card className="unified-analytics__card">
                    <Empty
                      description="Henüz bekleme ekranı verisi bulunmuyor"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                ) : (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Card 
                        title={
                          <div className="card-title">
                            <div className="card-title-icon card-title-icon--purple">
                              <BarChartOutlined />
                            </div>
                            <span>Etkileşim Metrikleri</span>
                          </div>
                        }
                        className="unified-analytics__card"
                      >
                        <div className="metric-grid">
                          <div className="metric-card">
                            <div className="metric-card__icon metric-card__icon--purple">
                              <SwapOutlined />
                            </div>
                            <div>
                              <div className="metric-card__title">Reels Swipe</div>
                              <div className="metric-card__subtitle">Toplam kaydırma</div>
                            </div>
                            <div className="metric-card__value">
                              {formatLargeNumber(waitingAnalytics?.engagement.reelsSwipeCount || 0)}
                            </div>
                          </div>

                          <div className="metric-card">
                            <div className="metric-card__icon metric-card__icon--green">
                              <ShoppingOutlined />
                            </div>
                            <div>
                              <div className="metric-card__title">Ürün Tıklama</div>
                              <div className="metric-card__subtitle">Toplam tıklama</div>
                            </div>
                            <div className="metric-card__value">
                              {formatLargeNumber(waitingAnalytics?.engagement.productClickCount || 0)}
                            </div>
                          </div>

                          <div className="metric-card">
                            <div className="metric-card__icon metric-card__icon--orange">
                              <ClockCircleOutlined />
                            </div>
                            <div>
                              <div className="metric-card__title">Video İzleme Süresi</div>
                              <div className="metric-card__subtitle">Ortalama süre</div>
                            </div>
                            <div className="metric-card__value">
                              {(waitingAnalytics?.engagement.avgVideoWatchTime || 0).toFixed(1)}s
                            </div>
                          </div>

                          <div className="metric-card">
                            <div className="metric-card__icon metric-card__icon--red">
                              <DislikeOutlined />
                            </div>
                            <div>
                              <div className="metric-card__title">Erken Çıkış Oranı</div>
                              <div className="metric-card__subtitle">Bekleme bitmeden çıkanlar</div>
                            </div>
                            <div className="metric-card__value">
                              %{(waitingAnalytics?.engagement.bounceRate || 0).toFixed(1)}
                            </div>
                          </div>
                        </div>

                        {/* Weekly Report Card */}
                        <div className="weekly-report-card">
                          <div className="weekly-report-card__icon">
                            <RiseOutlined />
                          </div>
                          <div className="weekly-report-card__content">
                            <div className="weekly-report-card__title">Haftalık Rapor Hazır!</div>
                            <div className="weekly-report-card__text">
                              Son 7 günlük analitik verilerinizi PDF olarak indirin.
                            </div>
                          </div>
                          <Button 
                            className="weekly-report-card__btn" 
                            icon={<DownloadOutlined />}
                            onClick={downloadWeeklyReport}
                            loading={downloadingReport}
                          >
                            {downloadingReport ? 'İndiriliyor...' : 'İndir (PDF)'}
                          </Button>
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Card 
                        title={
                          <div className="card-title">
                            <div className="card-title-icon card-title-icon--blue">
                              <LineChartOutlined />
                            </div>
                            <span>Event Detayları</span>
                            <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 13 }}>Son 24 Saat</Text>
                          </div>
                        }
                        className="unified-analytics__card"
                      >
                        <div className="event-grid">
                          {[
                            { key: 'waiting_started', label: 'Bekleme Başladı', icon: <PlayCircleOutlined />, colorClass: 'event-item__icon--purple' },
                            { key: 'waiting_ready', label: 'Sonuç Hazır', icon: <CheckCircleOutlined />, colorClass: 'event-item__icon--green' },
                            { key: 'continue_clicked', label: 'Devam Tıklandı', icon: <PlayCircleOutlined />, colorClass: 'event-item__icon--blue' },
                            { key: 'waiting_completed', label: 'Tamamlandı', icon: <CheckCircleOutlined />, colorClass: 'event-item__icon--green' },
                            { key: 'reels_swipe', label: 'Reels Swipe', icon: <SwapOutlined />, colorClass: 'event-item__icon--pink' },
                            { key: 'reels_view', label: 'Reels View', icon: <EyeOutlined />, colorClass: 'event-item__icon--purple' },
                            { key: 'product_click', label: 'Ürün Tıklama', icon: <ShoppingOutlined />, colorClass: 'event-item__icon--green' },
                            { key: 'chat_opened', label: 'Chat Açıldı', icon: <MessageOutlined />, colorClass: 'event-item__icon--pink' },
                          ].map((event) => (
                            <div key={event.key} className="event-item">
                              <div className={`event-item__icon ${event.colorClass}`}>
                                {event.icon}
                              </div>
                              <div className="event-item__value">
                                {formatLargeNumber(waitingAnalytics?.eventCounts[event.key as keyof typeof waitingAnalytics.eventCounts] || 0)}
                              </div>
                              <Text className="event-item__label">
                                {event.label}
                              </Text>
                            </div>
                          ))}
                        </div>
                      </Card>
                    </Col>
                  </Row>
                )}
              </>
            ),
          },
          {
            key: '2',
            label: (
              <span>
                <PieChartOutlined />
                Geri Bildirim Analizi
              </span>
            ),
            children: (
              <>
                {!hasFeedbackData ? (
                  <Card className="unified-analytics__card">
                    <Empty
                      description="Henüz geri bildirim verisi bulunmuyor"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                ) : (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={8}>
                      <Card className="unified-analytics__card feedback-summary-card">
                        <div className="feedback-summary">
                          <div className="feedback-summary__item feedback-summary__item--likes">
                            <LikeOutlined />
                            <div>
                              <div className="feedback-summary__value">{feedbackAnalytics?.likes || 0}</div>
                              <div className="feedback-summary__label">Beğeni</div>
                            </div>
                          </div>
                          <div className="feedback-summary__item feedback-summary__item--dislikes">
                            <DislikeOutlined />
                            <div>
                              <div className="feedback-summary__value">{feedbackAnalytics?.dislikes || 0}</div>
                              <div className="feedback-summary__label">Beğenmeme</div>
                            </div>
                          </div>
                        </div>
                        <div className="feedback-summary__rate">
                          <Progress
                            type="circle"
                            percent={Math.round(feedbackAnalytics?.likeRate || 0)}
                            strokeColor={getProgressColor(feedbackAnalytics?.likeRate || 0)}
                            format={(percent) => `${percent}%`}
                            size={120}
                          />
                          <Text type="secondary">Beğeni Oranı</Text>
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Card 
                        title={
                          <span className="card-title">
                            <DislikeOutlined style={{ color: '#EC4899' }} />
                            Beğenmeme Nedenleri
                          </span>
                        }
                        className="unified-analytics__card"
                      >
                        {feedbackAnalytics?.dislikeReasons && feedbackAnalytics.dislikeReasons.length > 0 ? (
                          <div className="reason-list">
                            {feedbackAnalytics.dislikeReasons.map((reason, index) => (
                              <div key={index} className="reason-item">
                                <div className="reason-item__header">
                                  <Text>{getReasonLabel(reason.reason)}</Text>
                                  <Text type="secondary">{reason.count} ({reason.percentage.toFixed(1)}%)</Text>
                                </div>
                                <Progress 
                                  percent={reason.percentage} 
                                  showInfo={false}
                                  strokeColor="#EC4899"
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Empty description="Henüz beğenmeme verisi yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Card 
                        title={
                          <span className="card-title">
                            <AppstoreOutlined style={{ color: '#3B82F6' }} />
                            Türe Göre Dağılım
                          </span>
                        }
                        className="unified-analytics__card"
                      >
                        {feedbackAnalytics?.byEntityType && feedbackAnalytics.byEntityType.length > 0 ? (
                          <div className="entity-list">
                            {feedbackAnalytics.byEntityType.map((item, index) => (
                              <div key={index} className="entity-item">
                                <div className="entity-item__icon">
                                  {getEntityTypeIcon(item.entityType)}
                                </div>
                                <div className="entity-item__info">
                                  <Text strong style={{ textTransform: 'capitalize' }}>
                                    {item.entityType === 'video' ? 'Video' : 
                                     item.entityType === 'avatar' ? 'Avatar' : 'Kombin'}
                                  </Text>
                                  <div className="entity-item__stats">
                                    <span className="entity-item__stat entity-item__stat--like">
                                      <LikeOutlined /> {item.likes}
                                    </span>
                                    <span className="entity-item__stat entity-item__stat--dislike">
                                      <DislikeOutlined /> {item.dislikes}
                                    </span>
                                    <span className="entity-item__stat">
                                      Toplam: {item.total}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Empty description="Henüz veri yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </Card>
                    </Col>
                  </Row>
                )}
              </>
            ),
          },
          {
            key: '3',
            label: (
              <span>
                <LineChartOutlined />
                Trend Grafiği
              </span>
            ),
            children: (
              <Card className="unified-analytics__card">
                {feedbackAnalytics?.trend && feedbackAnalytics.trend.length > 0 ? (
                  <div className="trend-chart">
                    <div className="trend-chart__bars">
                      {feedbackAnalytics.trend.map((day, index) => {
                        const maxValue = Math.max(...feedbackAnalytics.trend.map(d => Math.max(d.likes, d.dislikes))) || 1;
                        const likesHeight = (day.likes / maxValue) * 180;
                        const dislikesHeight = (day.dislikes / maxValue) * 180;
                        
                        return (
                          <div key={index} className="trend-chart__day">
                            <div className="trend-chart__bar-group">
                              <Tooltip title={`Beğeni: ${day.likes}`}>
                                <div 
                                  className="trend-chart__bar trend-chart__bar--likes"
                                  style={{ height: `${likesHeight}px` }}
                                />
                              </Tooltip>
                              <Tooltip title={`Beğenmeme: ${day.dislikes}`}>
                                <div 
                                  className="trend-chart__bar trend-chart__bar--dislikes"
                                  style={{ height: `${dislikesHeight}px` }}
                                />
                              </Tooltip>
                            </div>
                            {index % 5 === 0 && (
                              <Text type="secondary" className="trend-chart__label">
                                {new Date(day.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                              </Text>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="trend-chart__legend">
                      <div className="trend-chart__legend-item">
                        <div className="trend-chart__legend-color trend-chart__legend-color--likes" />
                        <Text type="secondary">Beğeni</Text>
                      </div>
                      <div className="trend-chart__legend-item">
                        <div className="trend-chart__legend-color trend-chart__legend-color--dislikes" />
                        <Text type="secondary">Beğenmeme</Text>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Empty description="Henüz trend verisi yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default UnifiedAnalyticsPage;
